import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  VaporThemeProvider,
  VaporPage,
  Title,
  Box,
  Spinner,
  TextField,
  Button,
  NotificationOverflow,
  AlertTitle,
  Toast,
  NotificationInline,
  Stack,
} from "@vapor/v3-components";
import { TextEyePassword } from "../components/TextEyePassword";
import { recoveryPasswordCheck } from "../services/authService";
import { changePassword } from "../services/changePasswordService";
import {
  ChangePasswordResponse,
  RecoverPasswordCheckResponse,
} from "../services/Models/AuthModel";
import { useTranslation } from "react-i18next";
import { useAuth } from "../Router/AuthProvider";
import { returnError } from "../utils/util";

const RecoveryPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate(); // Per navigare altrove se necessario
  const { performLogout } = useAuth();
  const { user: fullParam } = useParams<{ user: string }>();
  const lParam = fullParam ? fullParam.split(":") : ["", ""];
  const userName = useRef<string>(lParam[0]);
  const digestParam = useRef<string>(lParam[1]);
  const [recoveryPasswordCheched, setRecoveryPasswordCheched] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [renewPassword, setRenewPassword] = useState("");
  const [activeSave, setActiveSave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOK, setSaveOK] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const didRun = useRef(false);

  const onClose = () => {};

  const getRecoveryPasswordCheck = async () => {
    setLoading(true);
    try {
      const result: RecoverPasswordCheckResponse = await recoveryPasswordCheck(
        userName.current,
        digestParam.current
      );
      if (result && result.ResultCode !== "ERROR") {
        console.log("getRecoveryPasswordCheck successful:");
        setRecoveryPasswordCheched(true);
      } else {
        setError(
          result.DetailedMessage && result.DetailedMessage !== ""
            ? result.DetailedMessage
            : t("invalid-url-body")
        );
        console.error(
          "getRecoveryPasswordCheck Error :",
          result.DetailedMessage
        );
      }
    } catch (error) {
      setError(t("errore interno"));
      console.error("getRecoveryPasswordCheck Error :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!didRun.current) {
      didRun.current = true;
      getRecoveryPasswordCheck();
    }
  }, []);

  const callChangePassword = async () => {
    if (!loading) {
      setLoading(true);
      try {
        const result: ChangePasswordResponse = await changePassword(
          "",
          userName.current,
          "",
          newPassword,
          activationCode
        );
        if (result && result.ResultCode !== "ERROR") {
          console.log("changePassword successful:");
          setSaveOK(t("recovery-password"));
          onClose();
        } else {
          setError(t(returnError(result)));
          console.error("changePassword Error :", result.DetailedMessage);
        }
      } catch (error) {
        setError(t("errore interno"));
        console.error("changePassword Error :", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <VaporThemeProvider>
      <VaporPage>
        {/* <Title title="Gestione Redirezione" /> */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#f5f5f5",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 800,
              height: "100%",
              maxHeight: 580,
              minWidth: 100,
              minHeight: 100,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: 2,
              backgroundColor: "white",
              boxShadow: 3,
              borderRadius: 2,
            }}
          >
            <Title
              title={
                t("recovery-password") +
                " " +
                t("utente") +
                " : " +
                userName.current
              }
            />
            <TextField
              variant="outlined"
              label={t("activationCode")}
              helperText={t("activationCodeHelp")}
              id="activationCode"
              disabled={!recoveryPasswordCheched}
              style={{
                width: 400,
              }}
              value={activationCode}
              autoComplete="off" // Disabilita autofill
              onChange={(e) => {
                setActivationCode(e.target.value);
              }}
              autoFocus
            />
            <TextEyePassword
              variant="outlined"
              label={t("new-password-key1")}
              helperText={t("new-password-help-descr1")}
              id="newPassword"
              disabled={!recoveryPasswordCheched}
              style={{
                marginTop: 16,
                width: 400,
              }}
              value={newPassword}
              autoComplete="new-password" // Disabilita autofill
              onChange={(e) => {
                setNewPassword(e.target.value);
              }}
            />
            <TextEyePassword
              variant="outlined"
              label={t("new-password-key2")}
              helperText={
                renewPassword !== ""
                  ? newPassword !== renewPassword
                    ? t("no-password-match")
                    : t("new-password-help-descr2")
                  : t("new-password-help-descr2")
              }
              id="renewPassword"
              disabled={!recoveryPasswordCheched}
              style={{
                marginTop: 16,
                width: 400,
              }}
              value={renewPassword}
              autoComplete="new-password" // Disabilita autofill
              error={
                renewPassword !== ""
                  ? newPassword !== renewPassword
                    ? true
                    : undefined
                  : undefined
              }
              onChange={(e) => {
                setRenewPassword(e.target.value);
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "left",
                alignItems: "left",
                flexDirection: "row",
                marginTop: "36px",
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  callChangePassword();
                }}
                disabled={
                  activationCode === "" ||
                  newPassword === "" ||
                  newPassword !== renewPassword
                }
              >
                {t("change")}
              </Button>
            </Box>
            {/* {error && (
              <NotificationOverflow
                severity="error"
                onClose={() => setError(null)}
              >
                <AlertTitle>{t("error-header")}</AlertTitle>
                {error}
              </NotificationOverflow>
            )} */}
          </Box>
          <Toast
            open={saveOK ? true : false}
            anchorOrigin={{
              horizontal: "right",
              vertical: "top",
            }}
            // autoHideDuration={2000}
            onClose={() => {
              setSaveOK(null);
              //              navigate("/logout");
              performLogout();
            }}
          >
            <NotificationInline
              severity="success"
              onClose={() => {
                setSaveOK(null);
                //                navigate("/logout");
                performLogout();
              }}
            >
              <AlertTitle>{t("recovery-password")}</AlertTitle>
              {t("changePwdOK")}
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  mt: 3,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSaveOK(null);
                    //                    navigate("/logout");
                    performLogout();
                  }}
                >
                  {t("close")}
                </Button>
              </Stack>
            </NotificationInline>
          </Toast>
        </Box>
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)", // Sfondo semitrasparente opzionale
            }}
          >
            <Spinner size={64} />
          </Box>
        )}
        {error && (
          <NotificationOverflow severity="error" onClose={() => setError(null)}>
            <AlertTitle>{t("error-header")}</AlertTitle>
            {error}
          </NotificationOverflow>
        )}
        {/* <Toast
          open={saveOK ? true : false}
          anchorOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          autoHideDuration={2000}
          onClose={() => {
            setSaveOK(null);
            navigate("/");
          }}
        >
          <NotificationInline
            severity="success"
            onClose={() => {
              setSaveOK(null);
              navigate("/");
            }}
          >
            <AlertTitle>{saveOK}</AlertTitle>
            {t("changePwdOK")}
          </NotificationInline>
        </Toast> */}
      </VaporPage>
    </VaporThemeProvider>
  );
};

export default RecoveryPassword;
